import { Component, OnInit } from "@angular/core";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { CurrentUserChangedData, PatientLogin, UserService } from "@main/users";
import { IM1Service } from "@project/services/im1.service";
import { filter } from "rxjs";
import { NavigationEnd } from "@angular/router";

@Component({
    selector: "subject-access-request-warning",
    template: `
        <ng-container *ngIf="isPatient && isWarningVisible">
            <div class="sar-warning-wrapper" [class.collapsed]="isCollapsed">
                <p-messages severity="warn" styleClass="m-0">
                    <ng-template pTemplate>
                        <div class="flex flex-column">
                            <button class="mobile-toggle-btn" 
                                    (click)="toggleCollapse()"
                                    [attr.aria-expanded]="!isCollapsed"
                                    aria-label="Toggle medical record access information">
                                <i class="pi pi-exclamation-triangle mr-2"></i>
                                <span class="toggle-text">Can't See Your Full Medical Record?</span>
                                <i class="pi toggle-icon" [ngClass]="isCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'"></i>
                            </button>

                            <div class="collapsible-content">
                                <div class="font-bold desktop-title">
                                    <i class="pi pi-exclamation-triangle mr-2"></i>
                                    Can't See Your Full Medical Record in ORB? Here's What to Do
                                </div>

                                <div class="mt-1">
                                    As part of a government mandate, all prospective medical records from 2023 onwards should
                                    automatically appear in your ORB App.
                                    However, each GP surgery sets its own policy on how far back retrospective data is shared.
                                    Under UK GDPR and data protection regulations, you have the right to request full access to your
                                    medical record, this is called a Subject Access Request (SAR).
                                    If you do not see your complete record in the ORB App, you may need to request access from your GP.
                                    To do this, click the button below to download a <b>pre-written</b> request letter that you can provide to
                                    your GP. Once received, they have <b>one month</b> to process your request
                                </div>

                                <div class="flex justify-content-between align-items-center mt-2 action-buttons">
                                    <div>
                                        <a class="text-primary line-underline cursor-pointer select-none"
                                           [class.text-muted]="isGeneratingDoc"
                                           [class.cursor-not-allowed]="isGeneratingDoc"
                                           (click)="!isGeneratingDoc && generateRequestDoc()">
                                            Download Request Letter
                                            <span *ngIf="isGeneratingDoc" class="ml-2 pi-spin">?</span>
                                        </a>
                                    </div>
                                    <div class="flex button-group">
                                        <div class="mr-3">
                                            <button pButton
                                                    label="Don't Remind Me Again"
                                                    class="p-button-text p-button-primary"
                                                    (click)="dismissWithDoNotRemind()">
                                            </button>
                                        </div>
                                        <div>
                                            <button pButton
                                                    label="Dismiss"
                                                    class="p-button-text p-button-primary"
                                                    (click)="dismissWarning()">
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ng-template>
                </p-messages>
            </div>
        </ng-container>
    `,
    styleUrls: ["./subject-access-request-warning.component.scss"]
})
export class SubjectAccessRequestWarningComponent implements OnInit {
    private static isDismissed = false;

    isWarningVisible = false;
    isGeneratingDoc = false;
    isCollapsed = true;

    constructor(
        private appOnlineService: AppOnlineStateService,
        private patientService: PatientService,
        private userHelperService: UserHelperService,
        private userService: UserService,
        private im1Service: IM1Service
    ) {}

    ngOnInit(): void {
        this.checkVisibility();
    }

    get isPatient() {
        return this.userHelperService.isPatient;
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    async generateRequestDoc() {
        if (this.isGeneratingDoc) return;

        this.isGeneratingDoc = true;
        try {
            const result = await this.im1Service.generateSubjectAccessRequestPdf();
            window.open(result.url, "_blank");
        } finally {
            this.isGeneratingDoc = false;
        }
    }

    dismissWithDoNotRemind() {
        SubjectAccessRequestWarningComponent.isDismissed = true;
        this.patientService.updateDoNotRemindSarState(this.userHelperService.patientId, true).then();
        this.isWarningVisible = false;
    }

    dismissWarning() {
        SubjectAccessRequestWarningComponent.isDismissed = true;
        this.isWarningVisible = false;
    }

    private checkVisibility() {
        const isAppOnline = this.appOnlineService.isAppOnline;
        const doNotRemindSar = (this.userService.currentUser as PatientLogin)?.doNotRemindSarWarning;
        this.isWarningVisible = isAppOnline &&
            !SubjectAccessRequestWarningComponent.isDismissed &&
            this.isPatient &&
            !doNotRemindSar;
    }
}